// reimporting actions to mock inner function
// https://stackoverflow.com/questions/51269431/jest-mock-inner-function
import * as actions from './actions'
import * as types from './types'
import * as helpers from '../bulk-import/helpers'
import { apiService } from '../api/bulk-import-api.service'
import { BULK_IMPORT_STATIC_ERRORS } from '../bulk-import/bulk-import-errors'
import {
  getDeliveryBulkImportTableData,
  getShopifyOrdersTableData,
} from './selectors'

function featureFlagGogodelivery(value) {
  return {
    type: types.FEATUREFLAG_GOGODELIVERY_GGB,
    value,
  }
}

function deliveryDropoffTime({ instant, speed, same_day_day, same_day_night }) {
  return {
    type: types.DELIVERY_SET_DROPOFF_TIME,
    instant,
    speed,
    same_day_day,
    same_day_night,
  }
}

function deliverySetOrderDetailImageUrl(value) {
  return {
    type: types.DELIVERY_SET_ORDER_DETAIL_IMAGE_DATA_URL,
    value,
  }
}

function deliverySetBulkImportTableData(value) {
  return {
    type: types.DELIVERY_SET_BULK_IMPORT_TABLE_DATA,
    value,
  }
}

function deliveryEditBulkImportTableData({ value, rowId, column }) {
  return {
    type: types.DELIVERY_EDIT_BULK_IMPORT_TABLE_DATA,
    value,
    rowId,
    column,
  }
}

function deliveryBulkEditBulkImportTableData({ value, column, selectedRows }) {
  return {
    type: types.DELIVERY_BULK_EDIT_BULK_IMPORT_TABLE_DATA,
    value,
    column,
    selectedRows,
  }
}

function deliveryDeleteBulkImportTableData({ rowId, column }) {
  return {
    type: types.DELIVERY_DELETE_BULK_IMPORT_TABLE_DATA,
    rowId,
    column,
  }
}

function deliveryDeleteBulkImportTableDataRow(value) {
  return {
    type: types.DELIVERY_DELETE_BULK_IMPORT_TABLE_DATA_ROW,
    value,
  }
}

function deliverySetBulkImportQuotationTotal(value) {
  return {
    type: types.DELIVERY_SET_BULK_IMPORT_QUOTATION_TOTAL,
    value,
  }
}

function deliveryBulkImportShowLoadingScreen(value) {
  return {
    type: types.DELIVERY_BULK_IMPORT_SHOW_LOADING_SCREEN,
    value,
  }
}

function deliveryBulkImportUploadError(value) {
  return {
    type: types.DELIVERY_SET_BULK_IMPORT_UPLOAD_ERROR,
    value,
  }
}

function deliveryBulkImportAddressMatchInProgress(value) {
  return {
    type: types.DELIVERY_BULK_IMPORT_ADDRESS_MATCH_IN_PROGRESS,
    value,
  }
}

function deliveryBulkImportQuoteInProgress(value) {
  return {
    type: types.DELIVERY_BULK_IMPORT_SET_QUOTE_IN_PROGRESS,
    value,
  }
}

function deliveryBulkImportExcelParsingInProgress(value) {
  return {
    type: types.DELIVERY_BULK_IMPORT_SET_EXCEL_PARSING_IN_PROGRESS,
    value,
  }
}

const validateQuoteResponse = async (
  formattedData,
  frontendValidationErrors
) => {
  let backendError = {}
  let result

  try {
    result = await apiService.getQuote(formattedData)
  } catch (error) {
    backendError = error
    if (
      backendError.message ===
      BULK_IMPORT_STATIC_ERRORS.API_PROCESS_ORDER_FAILURE
    ) {
      Object.entries(frontendValidationErrors).forEach(([row, errorList]) => {
        if (Array.isArray(backendError.orderFailures[row])) {
          backendError.orderFailures[row].push(...errorList)
        } else {
          // eslint-disable-next-line no-param-reassign
          backendError.orderFailures[row] = errorList
        }
      })
      throw backendError
    }

    throw backendError
  }

  if (Object.keys(frontendValidationErrors).length > 0) {
    const error = new Error(BULK_IMPORT_STATIC_ERRORS.API_PROCESS_ORDER_FAILURE)
    error.orderFailures = frontendValidationErrors
    throw error
  }

  return result
}

function deliveryBulkImportGetQuote(formattedData) {
  return async (dispatch, getState) => {
    const state = getState()
    const orders = getDeliveryBulkImportTableData(state)

    const quotePayload = formattedData || { orders }
    quotePayload.orders.forEach(order => {
      if (typeof order.user_voucher_ids === 'number')
        order.user_voucher_ids = [order.user_voucher_ids]
    })
    // do not get quote if orders contains errors
    if (helpers.orderListContainsError(quotePayload.orders)) return

    let frontendValidationErrors = helpers.validatePickupTime(
      quotePayload.orders,
      {}
    )

    frontendValidationErrors = helpers.validateAddressDetail(
      quotePayload.orders,
      frontendValidationErrors
    )

    dispatch(deliveryBulkImportQuoteInProgress(true))

    try {
      const response = await validateQuoteResponse(
        quotePayload,
        frontendValidationErrors
      )
      const withCoordinates = helpers.parseCoordinates(response.data.orders)
      const parsedResponse = helpers.parseSizeToDimensions(withCoordinates)
      // for valid response need to add the shipper_address_warning back into the response
      const keepWarningsInOrderData = helpers.injectWarningIntoQuotationResponse(
        quotePayload.orders,
        parsedResponse
      )

      dispatch(deliverySetBulkImportTableData(keepWarningsInOrderData))
      dispatch(deliverySetBulkImportQuotationTotal(response.data.payment.total))
      return keepWarningsInOrderData
    } catch (error) {
      if (error.orderFailures && Object.keys(error.orderFailures).length > 0) {
        const submittedOrdersWithErrors = helpers
          .injectErrorsIntoOrderData(error.orderFailures, quotePayload.orders)
          .map(rowData => ({ price: null, ...rowData }))
        dispatch(deliverySetBulkImportTableData(submittedOrdersWithErrors))
        dispatch(deliverySetBulkImportQuotationTotal(0))
      }
      throw error
      // rethrow the error
      // so handleDocumentSelect can display modal / or scrollIntoView and do mixpanel tracking
    } finally {
      dispatch(deliveryBulkImportQuoteInProgress(false))
    }
  }
}

function getQuoteAndThenAddressSuggestion(formattedData) {
  return async (dispatch, getState) => {
    try {
      await dispatch(actions.deliveryBulkImportGetQuote(formattedData))
    } catch (error) {
      let errorToThrow = error
      //  call onUploadComplete so table is displayed immediately, and address match continues
      if (error?.response?.data?.status === 422) {
        dispatch(deliveryBulkImportAddressMatchInProgress(true))

        const updatedState = getState()
        const bulkOrderData = getDeliveryBulkImportTableData(updatedState)
        const suggestedAddresses = await helpers.getInvalidAddressAndReturnAddressSuggestions(
          bulkOrderData
        )
        const replacedWithSuggestedAddresses = helpers.replaceAddressWithSuggestions(
          bulkOrderData,
          suggestedAddresses
        )

        dispatch(deliverySetBulkImportTableData(replacedWithSuggestedAddresses))

        try {
          await dispatch(actions.deliveryBulkImportGetQuote())
        } catch (error) {
          errorToThrow = error
        } finally {
          dispatch(deliveryBulkImportAddressMatchInProgress(false))
        }
      }
      // rethrow the error
      // so handleDocumentSelect can display modal / or scrollIntoView and do mixpanel tracking
      throw errorToThrow
    }
  }
}

function shopifySetOrdersTableData(value) {
  return {
    type: types.SHOPIFY_SET_ORDERS_TABLE_DATA,
    value,
  }
}

function shopifyEditOrdersTableData({ value, rowId, column }) {
  return {
    type: types.SHOPIFY_EDIT_ORDERS_TABLE_DATA,
    value,
    rowId,
    column,
  }
}

function shopifyEditColumnsTableData({ value, column, selectedRows }) {
  return {
    type: types.SHOPIFY_EDIT_COLUMNS_ORDERS_TABLE_DATA,
    value,
    column,
    selectedRows,
  }
}

function shopifyDeleteOrdersTableData({ rowId, column }) {
  return {
    type: types.SHOPIFY_DELETE_ORDERS_TABLE_DATA,
    rowId,
    column,
  }
}

function shopifyDeleteOrdersTableDataRow(value) {
  return {
    type: types.SHOPIFY_DELETE_ORDERS_TABLE_DATA_ROW,
    value,
  }
}

function shopifySetOrdersQuotationTotal(value) {
  return {
    type: types.SHOPIFY_SET_ORDERS_TABLE_QUOTATION_TOTAL,
    value,
  }
}

function shopifyQuoteInProgress(value) {
  return {
    type: types.SHOPIFY_SET_QUOTE_IN_PROGRESS,
    value,
  }
}

function shopifyGetQuote(formattedData) {
  return async (dispatch, getState) => {
    const state = getState()
    const orders = getShopifyOrdersTableData(state)
    const quotePayload = formattedData || { orders }

    // do not get quote if orders contains errors
    if (helpers.orderListContainsError(quotePayload.orders)) return

    const frontendValidationErrors = helpers.validateForShopify(
      quotePayload.orders,
      { defaultErrors: {} }
    )

    dispatch(shopifyQuoteInProgress(true))

    try {
      const response = await validateQuoteResponse(
        quotePayload,
        frontendValidationErrors
      )
      const withCoordinates = helpers.parseCoordinates(response.data.orders)
      const parsedResponse = helpers.parseSizeToDimensions(withCoordinates)
      // for valid response need to add the shipper_address_warning back into the response
      const keepWarningsInOrderData = helpers.injectWarningIntoQuotationResponse(
        quotePayload.orders,
        parsedResponse
      )

      dispatch(shopifySetOrdersTableData(keepWarningsInOrderData))
      dispatch(shopifySetOrdersQuotationTotal(response.data.payment.total))
    } catch (error) {
      if (error.orderFailures && Object.keys(error.orderFailures).length > 0) {
        const submittedOrdersWithErrors = helpers
          .injectErrorsIntoOrderData(error.orderFailures, quotePayload.orders)
          .map(rowData => ({ price: null, ...rowData }))
        dispatch(shopifySetOrdersTableData(submittedOrdersWithErrors))
        dispatch(shopifySetOrdersQuotationTotal(0))
      }
      throw error
      // rethrow the error
      // so handleDocumentSelect can display modal / or scrollIntoView and do mixpanel tracking
    } finally {
      dispatch(shopifyQuoteInProgress(false))
    }
  }
}

export {
  featureFlagGogodelivery,
  deliveryDropoffTime,
  deliverySetOrderDetailImageUrl,
  deliverySetBulkImportTableData,
  deliveryEditBulkImportTableData,
  deliveryBulkEditBulkImportTableData,
  deliveryDeleteBulkImportTableDataRow,
  deliverySetBulkImportQuotationTotal,
  deliveryDeleteBulkImportTableData,
  deliveryBulkImportQuoteInProgress,
  deliveryBulkImportGetQuote,
  deliveryBulkImportAddressMatchInProgress,
  getQuoteAndThenAddressSuggestion,
  deliveryBulkImportUploadError,
  deliveryBulkImportShowLoadingScreen,
  deliveryBulkImportExcelParsingInProgress,
  shopifySetOrdersTableData,
  shopifyEditOrdersTableData,
  shopifyEditColumnsTableData,
  shopifyDeleteOrdersTableData,
  shopifyDeleteOrdersTableDataRow,
  shopifyGetQuote,
  shopifySetOrdersQuotationTotal,
  shopifyQuoteInProgress,
}
